import dayjs from 'dayjs';
import 'dayjs/locale/pl';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import axios from 'axios';
import Benefit from 'layout/atoms/Benefit';
import Button from 'layout/atoms/Button';
import Container from 'layout/atoms/Container';
import ContentBox from 'layout/atoms/ContentBox';
import Grid from 'layout/atoms/Grid';
import ListHeader from 'layout/atoms/ListHeader/ListHeader';
import Switch from 'layout/atoms/Switch';
import SubscriptionPlanBox from 'layout/molecules/SubscriptionPlanBox';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import { SubscriptionPeriod } from 'containers/Subscription/types';
import Loader from 'layout/molecules/Loader';
import { Environment } from 'src/Environment';
import { AppDispatch } from 'store';
import { getUserSubscription, selectUserSubscription } from 'store/userSlice';

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const SubscriptionPage = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { t } = useTranslation();
    const userSubscription = useSelector(selectUserSubscription);
    const [period, setPeriod] = useState<SubscriptionPeriod>('quarter');
    const currentPlanIsFree = userSubscription.plan === 'FREE';
    const currentPlanIsTrial =
        userSubscription.plan === 'PREMIUM' && userSubscription.isTrial;
    const currentPlanIsPremium =
        userSubscription.plan === 'PREMIUM' && !userSubscription.isTrial;
    const currentPlanIsNotPremium =
        userSubscription.plan !== 'PREMIUM' || userSubscription.isTrial;

    const query = useQuery();
    const callbackReason = query.get('reason');

    useEffect(() => {
        userSubscription.status === 'IDLE' && dispatch(getUserSubscription());
    }, [dispatch, userSubscription.status]);

    const startPayment = (event: any) => {
        axios
            .post(
                `${Environment.backendUrl}/api/v1/user/plan/purchase`,
                {
                    period,
                },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(
                            'hustleToken'
                        )}`,
                    },
                }
            )
            .then((response) => {
                if (response.data.redirectUrl) {
                    window.location.href = response.data.redirectUrl;
                }
            });
    };

    const freeBenefits = () => (
        <Grid columns="1" gap={12}>
            <Benefit active>{t('SUBSCRIPTION.BENEFIT_5_PROJECTS')}</Benefit>
            <Benefit active>{t('SUBSCRIPTION.BENEFIT_UNLIMITED_TASKS')}</Benefit>
            <Benefit active>{t('SUBSCRIPTION.BENEFIT_2_AUTOMATIONS')}</Benefit>
            <Benefit>{t('SUBSCRIPTION.BENEFIT_SHARE')}</Benefit>
            <Benefit>{t('SUBSCRIPTION.BENEFIT_SUPPORT')}</Benefit>
        </Grid>
    );

    const premiumBenefits = () => (
        <Grid columns="2" gap={12}>
            <Benefit active>{t('SUBSCRIPTION.BENEFIT_UNLIMITED_PROJECTS')}</Benefit>
            <Benefit active>{t('SUBSCRIPTION.BENEFIT_UNLIMITED_TASKS')}</Benefit>
            <Benefit active>{t('SUBSCRIPTION.BENEFIT_UNLIMITED_AUTOMATIONS')}</Benefit>
            <Benefit active>{t('SUBSCRIPTION.BENEFIT_SHARE')}</Benefit>
            <Benefit active>{t('SUBSCRIPTION.BENEFIT_SUPPORT')}</Benefit>
            <Benefit active={period === 'year'}>{t('SUBSCRIPTION.SAVING_20')}</Benefit>
        </Grid>
    );

    if (
        userSubscription.status === 'IDLE' ||
        userSubscription.status === 'LOADING'
    ) {
        return (
            <Container size="s">
                <ListHeader text={t('SUBSCRIPTION.TITLE')} />
                <Loader />
            </Container>
        );
    }

    return (
        <>
            <Container size="s">
                {callbackReason === 'cancel' && <>{t('SUBSCRIPTION.PAYMENT_CANCELED')}</>}
                <Grid columns="1" gap={16}>
                    <ListHeader text={t('SUBSCRIPTION.TITLE')} />
                    <Grid columns="1" gap={16}>
                        <ContentBox>
                            <SubscriptionPlanBox
                                id="001"
                                monthPrice={userSubscription.plan || ''}
                                name={
                                    currentPlanIsTrial ? (
                                        <>
                                            <strong>{t('SUBSCRIPTION.FREE_TRIAL')}</strong>
                                        </>
                                    ) : null
                                }
                                pricePeriod={period}
                                current
                                benefits={
                                    !currentPlanIsFree && (
                                        <p>
                                            {t('SUBSCRIPTION.VALID_UNTIL')}{' '}
                                            <strong>
                                                {dayjs(
                                                    currentPlanIsTrial
                                                        ? userSubscription.trialEndDate
                                                        : userSubscription.expirationDate
                                                ).format('D MMMM YYYY')}
                                            </strong>
                                        </p>
                                    )
                                }
                                buttons={
                                    <>
                                        {currentPlanIsNotPremium && (
                                            <Button
                                                size="xxl"
                                                theme="primary"
                                                onClick={startPayment}
                                            >
                                                {t('SUBSCRIPTION.UPGRADE_TO_PREMIUM')}
                                            </Button>
                                        )}
                                    </>
                                }
                            />
                        </ContentBox>
                    </Grid>
                </Grid>
            </Container>
            <Container size="l">
                <Grid columns="1" gap={16}>
                    <ListHeader text={t('SUBSCRIPTION.PRICING')} />
                    <Switch
                        name="period"
                        value={period === 'year'}
                        label={t('SUBSCRIPTION.BILLED_ANNUALLY')}
                        leftLabel={t('SUBSCRIPTION.BILLED_QUARTERLY')}
                        onChange={() =>
                            setPeriod(period === 'quarter' ? 'year' : 'quarter')
                        }
                    />
                    <Grid columns="5_7" gap={16}>
                        <ContentBox>
                            <SubscriptionPlanBox
                                id="001"
                                name={
                                    <>
                                        <strong>{t('SUBSCRIPTION.FREE')}</strong>
                                    </>
                                }
                                monthPrice="0$"
                                pricePeriod={period}
                                benefits={freeBenefits()}
                            />
                        </ContentBox>
                        <ContentBox>
                            <SubscriptionPlanBox
                                id="002"
                                name={
                                    <>
                                        <strong>{t('SUBSCRIPTION.PREMIUM')}</strong>
                                    </>
                                }
                                monthPrice="5$"
                                annualPrice="4$"
                                pricePeriod={period}
                                benefits={premiumBenefits()}
                                buttons={
                                    currentPlanIsNotPremium && (
                                        <Button
                                            size="xxl"
                                            theme="primary"
                                            onClick={startPayment}
                                        >
                                            {t('SUBSCRIPTION.UPGRADE_TO_PREMIUM')}
                                        </Button>
                                    )
                                }
                            />
                        </ContentBox>
                    </Grid>
                </Grid>
            </Container>
        </>
    );
};

export default SubscriptionPage;
